import {
  LayoutGrid,
  Shield,
  ShieldAlert,
  Columns3Cog,
  Layers,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import { useRouter } from '@uirouter/react';

import { Link } from '@@/Link';
import { Button } from '@@/buttons';

import { EnvironmentGroup } from '../../types';
import { PlatformBadge } from '../../components/PlatformBadge';
import { EnvironmentTypeBreakdown } from '../../components/EnvironmentTypeBreakdown';
import { isUngoverned } from '../../utils/getPlatformLabel';

interface Props {
  group: EnvironmentGroup;
}

export function EnvironmentGroupRow({ group }: Props) {
  const router = useRouter();
  const policyCount = group.Policies?.length ?? 0;
  const policyLabel = `${policyCount} ${
    policyCount === 1 ? 'policy' : 'policies'
  } attached`;
  const ungoverned = isUngoverned(group);

  function handleRowClick() {
    if (!ungoverned) {
      router.stateService.go('portainer.groups.group', { id: group.Id });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!ungoverned && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleRowClick();
    }
  }

  return (
    <div
      className={clsx(
        'group flex items-center gap-5 border-0 border-t border-solid border-gray-4 px-5 py-4 transition-colors',
        ungoverned
          ? 'cursor-default bg-gray-2 th-highcontrast:bg-gray-10 th-dark:bg-gray-10'
          : 'cursor-pointer hover:bg-gray-2 th-dark:hover:bg-gray-9',
        'th-dark:border-gray-8'
      )}
      data-cy={`environment-group-row-${group.Name}`}
      onClick={ungoverned ? undefined : handleRowClick}
      onKeyDown={ungoverned ? undefined : handleKeyDown}
      role={ungoverned ? undefined : 'button'}
      tabIndex={ungoverned ? undefined : 0}
    >
      <div
        className={clsx(
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl',
          ungoverned
            ? 'bg-warning-7 th-dark:bg-warning-10'
            : 'bg-group-accent-3 th-dark:bg-group-accent-10'
        )}
      >
        {ungoverned ? (
          <Columns3Cog
            className={clsx(
              'h-6 w-6',
              '!text-warning-4 th-dark:text-warning-3'
            )}
          />
        ) : (
          <LayoutGrid
            className={clsx(
              'h-6 w-6',
              '!text-group-accent-8 th-dark:!text-group-accent-2'
            )}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'text-sm font-bold',
              ungoverned
                ? 'text-warning-7 th-dark:text-warning-3'
                : 'text-gray-8 th-highcontrast:text-white th-dark:text-gray-2'
            )}
            data-cy={`environment-group-name-${group.Name}`}
          >
            {group.Name}
          </span>
          {!ungoverned && <PlatformBadge group={group} />}
        </div>

        <div
          className={clsx(
            'mt-1 flex items-center gap-4 text-xs',
            ungoverned
              ? 'text-warning-7 th-dark:text-warning-4'
              : 'text-gray-7 th-highcontrast:text-white group-hover:th-highcontrast:text-gray-11 th-dark:text-gray-5'
          )}
        >
          <EnvironmentTypeBreakdown group={group} />
          {!ungoverned && (
            <span
              className={clsx(
                'flex items-center gap-1.5 text-xs font-semibold',
                policyCount === 0
                  ? 'th-highcontrast:text-warning-5 group-hover:th-highcontrast:text-warning-8 th-dark:text-warning-3'
                  : 'text-gray-7 th-highcontrast:text-white group-hover:th-highcontrast:text-gray-11 th-dark:text-gray-5'
              )}
            >
              {policyCount === 0 ? (
                <ShieldAlert className="h-3.5 w-3.5" />
              ) : (
                <Shield className="h-3.5 w-3.5" />
              )}
              <span data-cy={`environment-group-policies_${group.Name}`}>
                {policyLabel}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {!ungoverned && (
        <div className="flex items-center gap-1">
          <Button
            as={Link}
            props={{
              to: 'portainer.policies',
              params: { id: group.Id },
            }}
            color="link"
            icon={Layers}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            data-cy={`manage-policies-button_${group.Name}`}
          >
            Manage policies
          </Button>
          <Button
            as={Link}
            props={{
              to: 'portainer.groups.group',
              params: { id: group.Id, tab: 'access' },
            }}
            color="link"
            icon={Users}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            data-cy={`manage-access-button_${group.Name}`}
          >
            Manage access
          </Button>
        </div>
      )}
    </div>
  );
}
